/**
 * Crop + re-encode helper for staff avatars.
 *
 * Whatever the source size (a 10MB phone photo included), the output is always
 * a 300x300 JPEG at q=0.8 — the same shape the POS app produces — so the
 * payload sent to `staff/update-avatar` stays ~20-60KB.
 */

import { removeBase64Prefix } from './removeBase64Prefix';

/** Output edge in pixels; matches POS STAFF_CONSTANTS.IMAGE_PICKER. */
export const AVATAR_SIZE = 300;
/** JPEG quality; matches POS COMPRESS_IMAGE_QUALITY. */
export const AVATAR_JPEG_QUALITY = 0.8;

/** Pixel rectangle in source-image coordinates (react-easy-crop's croppedAreaPixels). */
export interface CropAreaPixels {
	x: number;
	y: number;
	width: number;
	height: number;
}

/** Decodes an object URL / data URL into an HTMLImageElement. */
export const loadImage = (src: string): Promise<HTMLImageElement> =>
	new Promise((resolve, reject) => {
		const img = new Image();
		img.onload = () => resolve(img);
		img.onerror = () => reject(new Error('Unsupported image format'));
		img.src = src;
	});

/**
 * Draws `area` of the source image onto a 300x300 canvas and returns the JPEG
 * base64 WITHOUT the `data:` prefix (the BE strips it anyway, but sending it
 * bare keeps the payload honest).
 */
export const cropAvatarImage = async (src: string, area: CropAreaPixels): Promise<string> => {
	const img = await loadImage(src);

	const canvas = document.createElement('canvas');
	canvas.width = AVATAR_SIZE;
	canvas.height = AVATAR_SIZE;
	const ctx = canvas.getContext('2d');
	if (!ctx) throw new Error('Canvas is not supported');

	// JPEG has no alpha: without this, transparent PNG areas turn black.
	ctx.fillStyle = '#fff';
	ctx.fillRect(0, 0, AVATAR_SIZE, AVATAR_SIZE);
	ctx.imageSmoothingQuality = 'high';
	ctx.drawImage(img, area.x, area.y, area.width, area.height, 0, 0, AVATAR_SIZE, AVATAR_SIZE);

	const blob = await new Promise<Blob>((resolve, reject) => {
		canvas.toBlob(
			b => (b ? resolve(b) : reject(new Error('Cannot encode image'))),
			'image/jpeg',
			AVATAR_JPEG_QUALITY
		);
	});

	const dataUrl = await new Promise<string>((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = () => resolve(reader.result as string);
		reader.onerror = () => reject(new Error('Cannot encode image'));
		reader.readAsDataURL(blob);
	});
	return removeBase64Prefix(dataUrl);
};
